export const CONTENT_LABELS = [
  'Hook',
  'Pain',
  'Solution',
  'Benefit',
  'Aspirational Outcome',
  'CTA',
  'Product Demo',
  'Talking Head',
  'Testimonial',
  'Lifestyle',
  'Unboxing',
  'UGC',
  'Transition',
  'Scene',
] as const

export type ContentLabel = (typeof CONTENT_LABELS)[number]

export const CONTENT_LABEL_COLORS: Record<ContentLabel, { bg: string; text: string }> = {
  Hook: { bg: 'bg-rose-100', text: 'text-rose-700' },
  Pain: { bg: 'bg-orange-100', text: 'text-orange-700' },
  Solution: { bg: 'bg-blue-100', text: 'text-blue-700' },
  Benefit: { bg: 'bg-emerald-100', text: 'text-emerald-700' },
  'Aspirational Outcome': { bg: 'bg-violet-100', text: 'text-violet-700' },
  CTA: { bg: 'bg-amber-100', text: 'text-amber-700' },
  'Product Demo': { bg: 'bg-cyan-100', text: 'text-cyan-700' },
  'Talking Head': { bg: 'bg-pink-100', text: 'text-pink-700' },
  Testimonial: { bg: 'bg-indigo-100', text: 'text-indigo-700' },
  Lifestyle: { bg: 'bg-teal-100', text: 'text-teal-700' },
  Unboxing: { bg: 'bg-yellow-100', text: 'text-yellow-700' },
  UGC: { bg: 'bg-fuchsia-100', text: 'text-fuchsia-700' },
  Transition: { bg: 'bg-gray-100', text: 'text-gray-600' },
  Scene: { bg: 'bg-slate-100', text: 'text-slate-600' },
}

export type VisualTreatment =
  | 'physical_pain'
  | 'situational_pain'
  | 'talking_head'
  | 'hands_on_test'
  | 'product_demo'
  | 'lifestyle_context'
  | 'selfie_style'
  | 'animated_overlay'
  | 'unbox'
  | 'product_visual_hook'
  | 'visual_treatment'
  | 'split_screen_vertical'
  | 'split_screen_horizontal'
  | 'green_screen'
  | 'stitch_react'
  | 'transformation'
  | 'jump_cut'

export const VISUAL_TREATMENT_OPTIONS: { value: VisualTreatment; label: string }[] = [
  { value: 'situational_pain', label: 'Situational Pain' },
  { value: 'physical_pain', label: 'Physical Pain' },
  { value: 'visual_treatment', label: 'Visual Treatment' },
  { value: 'split_screen_vertical', label: 'Split Screen (vertical)' },
  { value: 'split_screen_horizontal', label: 'Split Screen (horizontal)' },
  { value: 'green_screen', label: 'Green Screen' },
  { value: 'animated_overlay', label: 'Animated Overlay' },
  { value: 'stitch_react', label: 'Stitch / React' },
  { value: 'transformation', label: 'Transformation' },
  { value: 'jump_cut', label: 'Jump Cut' },
  { value: 'talking_head', label: 'Talking Head' },
  { value: 'hands_on_test', label: 'Hands-on Test' },
  { value: 'product_demo', label: 'Product Demo' },
  { value: 'lifestyle_context', label: 'Lifestyle Context' },
  { value: 'selfie_style', label: 'Selfie Style' },
  { value: 'unbox', label: 'Unboxing' },
  { value: 'product_visual_hook', label: 'Product Visual Hook' },
]

export interface StoryboardFrameData {
  id: string
  image: string
  timeStart: string
  timeEnd: string
  label: string
  description?: string
  audioText?: string
  textOverlay?: string
  visualTreatment?: VisualTreatment
  videoUrl?: string
  sourceVideoId?: string
  /** Clip start time in seconds for trimming */
  clipStartSec?: number
  /** Clip end time in seconds for trimming */
  clipEndSec?: number
  /** AI-generated footage specifications for matching */
  aiFootages?: Array<{
    role: string
    description: string
    transcript?: string
    tags: {
      actiontype?: string
      shotsize?: string
      pointofview?: string
      quality?: string
      voice?: string
      broll?: string
    }
  }>
  /** Scene handoff information for transitions */
  handoff?: {
    next_block: string
    cue: string
    notes?: string
  }
}

export interface StoryboardVersion {
  id: number
  name: string
  date: string
}

export type CleanStatus = 'pending' | 'processing' | 'ready' | 'failed'

export interface FootageData {
  id: string
  filename: string
  thumbnail: string
  duration: string
  description?: string
  category?: string
  cleanStatus?: CleanStatus
  cleanVersionS3Key?: string
  tags?: {
    actiontype?: string
    brand?: string
    broll?: string
    date?: string
    hook?: string
    model?: string
  }
}

export interface BriefData {
  productName: string
  targetAudience: string
  mainBenefit: string
  painPoints: string[]
  tone: string
  callToAction: string
  keyMessages: string[]
  videoLength: string
  platform: string
}

export interface StoryboardSettings {
  name: string
  createdAt: string
  lastModified: string
  aspectRatio: string
  duration: string
  targetPlatform: string
  style: string
  audioTrack: string
  language: string
  status: string
}

/**
 * Content role values from AI analysis
 */
export type ContentRole =
  | 'hook'
  | 'pain'
  | 'solution'
  | 'cta'
  | 'lifestyle'
  | 'ugc'
  | 'transition'
  | 'product_demo'
  | 'testimonial'
  | 'unboxing'
  | 'social_proof'
  | 'first_person'
  | 'situational'

/**
 * Maps backend contentRole to UI ContentLabel
 */
export const CONTENT_ROLE_TO_LABEL: Record<ContentRole, ContentLabel> = {
  hook: 'Hook',
  pain: 'Pain',
  solution: 'Solution',
  cta: 'CTA',
  lifestyle: 'Lifestyle',
  ugc: 'UGC',
  transition: 'Transition',
  product_demo: 'Product Demo',
  testimonial: 'Testimonial',
  unboxing: 'Unboxing',
  social_proof: 'Testimonial',
  first_person: 'UGC',
  situational: 'Scene',
}

/**
 * Scene from Gemini AI Storyboard Generator
 */
export interface GeminiScene {
  sceneNumber: number
  startTime: number
  endTime: number
  description: string
  contentRole: ContentRole // Primary field for scene classification
  visualType: string
  audio: string
  textOverlay: string
  transitions: string
  camera: string
  mood: string
  keyElements: string[]
  voice?: 'voiceover' | 'on_camera' | 'text_only' | 'silent'
  pointOfView?: 'first_person' | 'third_person' | 'pov_mirror' | 'overhead'
  suggestedDuration?: number
}

/**
 * Maps a Gemini AI Scene to StoryboardFrameData for the editor
 */
export function geminiSceneToFrame(scene: GeminiScene, index: number): StoryboardFrameData {
  // Format time as MM:SS
  const formatTime = (seconds: number): string => {
    if (!Number.isFinite(seconds) || seconds < 0) return '0:00'
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  // Map visualType to VisualTreatment if possible
  const mapVisualType = (visualType: string): VisualTreatment | undefined => {
    const normalized = visualType.toLowerCase().replace(/[\s-]/g, '_')
    const validTreatments: VisualTreatment[] = [
      'physical_pain',
      'situational_pain',
      'talking_head',
      'hands_on_test',
      'product_demo',
      'lifestyle_context',
      'selfie_style',
      'animated_overlay',
      'unbox',
      'product_visual_hook',
    ]
    return validTreatments.find(t => normalized.includes(t.replace('_', ''))) || undefined
  }

  // Get label from contentRole (primary) or fall back to visual type inference
  const getLabel = (): ContentLabel => {
    // If contentRole is provided, use it directly
    if (scene.contentRole && CONTENT_ROLE_TO_LABEL[scene.contentRole]) {
      return CONTENT_ROLE_TO_LABEL[scene.contentRole]
    }

    // Fallback: infer from visualType and description
    const combined = `${scene.visualType} ${scene.description} ${scene.audio}`.toLowerCase()

    // First scene is typically a hook
    if (index === 0) return 'Hook'

    // CTA detection
    if (combined.includes('cta') || combined.includes('compra') || combined.includes('buy') ||
        combined.includes('shop') || combined.includes('ahora') || combined.includes('now') ||
        combined.includes('link') || combined.includes('click')) return 'CTA'

    // Pain detection
    if (combined.includes('pain') || combined.includes('problem') || combined.includes('frustrat') ||
        combined.includes('tired') || combined.includes('hate') || combined.includes('struggle')) return 'Pain'

    // Solution detection
    if (combined.includes('solution') || combined.includes('solv') || combined.includes('finally') ||
        combined.includes('works') || combined.includes('transform')) return 'Solution'

    // Other categories
    if (combined.includes('product') || combined.includes('demo')) return 'Product Demo'
    if (combined.includes('testimonial') || combined.includes('review')) return 'Testimonial'
    if (combined.includes('lifestyle')) return 'Lifestyle'
    if (combined.includes('unbox')) return 'Unboxing'
    if (combined.includes('ugc') || combined.includes('selfie') || combined.includes('first_person')) return 'UGC'
    if (combined.includes('transition') || combined.includes('broll')) return 'Transition'

    return 'Scene'
  }

  return {
    id: `gemini-scene-${scene.sceneNumber}-${index}`,
    image: '', // No thumbnail from AI - user will select footage
    timeStart: formatTime(scene.startTime),
    timeEnd: formatTime(scene.endTime),
    label: getLabel(),
    description: scene.description,
    audioText: scene.audio,
    textOverlay: scene.textOverlay,
    visualTreatment: mapVisualType(scene.visualType),
  }
}

function captureFrameAt(video: HTMLVideoElement, timeSec: number, maxWidth = 320): Promise<string> {
  return new Promise((resolve, reject) => {
    const clampedTime = Math.max(0, Math.min(timeSec, video.duration || timeSec))
    video.currentTime = clampedTime

    const onSeeked = () => {
      video.removeEventListener('seeked', onSeeked)
      try {
        const scale = Math.min(1, maxWidth / video.videoWidth)
        const w = Math.round(video.videoWidth * scale)
        const h = Math.round(video.videoHeight * scale)
        const canvas = document.createElement('canvas')
        canvas.width = w
        canvas.height = h
        const ctx = canvas.getContext('2d')
        if (!ctx) return reject(new Error('Canvas context unavailable'))
        ctx.drawImage(video, 0, 0, w, h)
        resolve(canvas.toDataURL('image/jpeg', 0.7))
      } catch (err) {
        reject(err)
      }
    }
    video.addEventListener('seeked', onSeeked)
  })
}

// ========================================
// Enhanced AI Storyboard Structure
// ========================================

/**
 * Tags for footage analysis - used for intelligent clip matching
 */
export interface FootageTags {
  /** Action type: PDET (product detail), PD (product demo), BNF (benefit), PAIN (pain point), etc. */
  actiontype?: string
  /** Shot size: CLUP (close-up), MED (medium), WIDE, ECU (extreme close-up) */
  shotsize?: string
  /** Point of view: FIRST (first person), THIRD (third person), POV */
  pointofview?: string
  /** Quality: HIFI (high fidelity), LOFI (low fidelity) */
  quality?: string
  /** Voice: NOVOICE, MODELTALKING, VOICEOVER */
  voice?: string
  /** B-roll: YES, NO */
  broll?: string
}

/**
 * Describes a single footage element within a scene
 */
export interface AIFootageDescription {
  /** Role of this footage: Main, Left, Right (for split screens), Background, etc. */
  role: string
  /** Detailed visual description of what should appear in this footage */
  description: string
  /** Transcript or audio content for this footage segment */
  transcript?: string
  /** Tags for matching with company video library */
  tags: FootageTags
}

/**
 * Handoff information for transitioning to the next scene
 */
export interface AISceneHandoff {
  /** The type of the next scene/block */
  next_block: string
  /** Visual cue for the transition */
  cue: string
  /** Additional notes for the transition */
  notes?: string
}

/**
 * Enhanced AI Scene structure with detailed footage specifications
 */
export interface AIStoryboardScene {
  /** Start time in seconds */
  start_time: number
  /** End time in seconds */
  end_time: number
  /** Whether this is a hook scene */
  hook: boolean
  /** Structure type: Hook, Solution, Benefit, CTA, etc. */
  structure: string
  /** Visual treatment: Split Screen, Talking Head, Lifestyle Context, etc. */
  visual_treatment: string
  /** Text overlays for this scene */
  text_overlays: string[]
  /** Footage descriptions for this scene (can have multiple for split screens) */
  footages: AIFootageDescription[]
  /** Handoff information for transitioning to the next scene */
  handoff?: AISceneHandoff
}

/**
 * Full AI storyboard response structure
 */
export interface AIStoryboardResponse {
  status: 'success' | 'error'
  response: AIStoryboardScene[]
}

/**
 * Maps visual treatment string to VisualTreatment enum
 */
function mapAIVisualTreatment(visualTreatment: string): VisualTreatment | undefined {
  const mapping: Record<string, VisualTreatment> = {
    'split screen': 'split_screen_vertical',
    'split screen vertical': 'split_screen_vertical',
    'split screen horizontal': 'split_screen_horizontal',
    'talking head': 'talking_head',
    'lifestyle context': 'lifestyle_context',
    'product demo': 'product_demo',
    'hands-on test': 'hands_on_test',
    'hands on test': 'hands_on_test',
    'selfie style': 'selfie_style',
    'unboxing': 'unbox',
    'unbox': 'unbox',
    'animated overlay': 'animated_overlay',
    'green screen': 'green_screen',
    'stitch react': 'stitch_react',
    'transformation': 'transformation',
    'jump cut': 'jump_cut',
  }
  const normalized = visualTreatment.toLowerCase()
  return mapping[normalized] || undefined
}

/**
 * Maps structure string to ContentLabel
 */
function mapAIStructureToLabel(structure: string): ContentLabel {
  const mapping: Record<string, ContentLabel> = {
    'hook': 'Hook',
    'pain': 'Pain',
    'solution': 'Solution',
    'benefit': 'Benefit',
    'cta': 'CTA',
    'product demo': 'Product Demo',
    'talking head': 'Talking Head',
    'testimonial': 'Testimonial',
    'lifestyle': 'Lifestyle',
    'unboxing': 'Unboxing',
    'ugc': 'UGC',
    'transition': 'Transition',
  }
  const normalized = structure.toLowerCase()
  return mapping[normalized] || 'Scene'
}

/**
 * Converts an AI Storyboard Scene to StoryboardFrameData for the editor
 */
export function aiSceneToFrame(scene: AIStoryboardScene, index: number): StoryboardFrameData {
  // Format time as M:SS
  const formatTime = (seconds: number): string => {
    if (!Number.isFinite(seconds) || seconds < 0) return '0:00'
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  // Combine footage descriptions for the main description
  const mainFootage = scene.footages.find(f => f.role === 'Main') || scene.footages[0]
  const description = mainFootage?.description || ''

  // Combine text overlays
  const textOverlay = scene.text_overlays.join('\n')

  // Get transcript from main footage as audio text
  const audioText = mainFootage?.transcript || ''

  // Convert AI footages to the frame format
  const aiFootages = scene.footages.map(f => ({
    role: f.role,
    description: f.description,
    transcript: f.transcript,
    tags: {
      actiontype: f.tags.actiontype,
      shotsize: f.tags.shotsize,
      pointofview: f.tags.pointofview,
      quality: f.tags.quality,
      voice: f.tags.voice,
      broll: f.tags.broll,
    },
  }))

  return {
    id: `ai-scene-${index}-${Date.now()}`,
    image: '', // No thumbnail from AI - user will select footage
    timeStart: formatTime(scene.start_time),
    timeEnd: formatTime(scene.end_time),
    label: mapAIStructureToLabel(scene.structure),
    description,
    audioText,
    textOverlay,
    visualTreatment: mapAIVisualTreatment(scene.visual_treatment),
    aiFootages,
    handoff: scene.handoff,
  }
}

/**
 * Converts a full AI storyboard response to StoryboardFrameData array
 */
export function aiStoryboardToFrames(response: AIStoryboardResponse): StoryboardFrameData[] {
  if (response.status !== 'success' || !response.response) {
    return []
  }
  return response.response.map((scene, index) => aiSceneToFrame(scene, index))
}

/**
 * Extracts footage tags from AI storyboard for matching
 */
export function extractFootageTagsFromScene(scene: AIStoryboardScene): FootageTags[] {
  return scene.footages.map(f => f.tags)
}

/**
 * Extracts a thumbnail frame from a video file for each scene at its midpoint.
 */
export async function extractVideoFrames(
  file: File,
  scenes: GeminiScene[]
): Promise<Map<number, string>> {
  const result = new Map<number, string>()
  if (!scenes.length) return result

  const video = document.createElement('video')
  video.muted = true
  video.playsInline = true
  video.preload = 'auto'
  const objectUrl = URL.createObjectURL(file)
  video.src = objectUrl

  try {
    await new Promise<void>((resolve, reject) => {
      video.addEventListener('loadeddata', () => resolve(), { once: true })
      video.addEventListener('error', () => reject(new Error('Video load failed')), { once: true })
    })

    for (let i = 0; i < scenes.length; i++) {
      const scene = scenes[i]
      const midpoint = (scene.startTime + scene.endTime) / 2
      try {
        const dataUrl = await captureFrameAt(video, midpoint)
        result.set(i, dataUrl)
      } catch {
        // Skip frames that fail to capture
      }
    }
  } finally {
    URL.revokeObjectURL(objectUrl)
    video.src = ''
  }

  return result
}

/**
 * Extracts a thumbnail frame from a video URL for each scene at its midpoint.
 * Works with cross-origin videos that allow CORS.
 * Returns empty map if CORS prevents frame extraction.
 */
export async function extractVideoFramesFromUrl(
  videoUrl: string,
  scenes: GeminiScene[]
): Promise<Map<number, string>> {
  const result = new Map<number, string>()
  if (!scenes.length || !videoUrl) return result

  const video = document.createElement('video')
  video.muted = true
  video.playsInline = true
  video.preload = 'auto'
  video.crossOrigin = 'anonymous' // Required for cross-origin canvas access
  video.src = videoUrl

  try {
    await new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Video load timeout'))
      }, 30000) // 30 second timeout

      video.addEventListener('loadeddata', () => {
        clearTimeout(timeout)
        resolve()
      }, { once: true })
      video.addEventListener('error', (e) => {
        clearTimeout(timeout)
        reject(new Error(`Video load failed: ${e.type}`))
      }, { once: true })
    })

    console.log('[extractVideoFramesFromUrl] Video loaded, duration:', video.duration, 'Extracting', scenes.length, 'frames')

    for (let i = 0; i < scenes.length; i++) {
      const scene = scenes[i]
      const midpoint = (scene.startTime + scene.endTime) / 2
      try {
        const dataUrl = await captureFrameAt(video, midpoint)
        result.set(i, dataUrl)
      } catch (err) {
        // CORS or other error - log but continue
        console.warn(`[extractVideoFramesFromUrl] Failed to capture frame ${i} at ${midpoint}s:`, err)
      }
    }

    console.log('[extractVideoFramesFromUrl] Extracted', result.size, 'of', scenes.length, 'frames')
  } catch (loadErr) {
    console.warn('[extractVideoFramesFromUrl] Video load failed:', loadErr)
  } finally {
    video.src = ''
  }

  return result
}

/**
 * Extracts a single thumbnail frame from a video URL at a specific timestamp.
 * Returns null if extraction fails (e.g., due to CORS).
 */
export async function extractSingleFrameFromUrl(
  videoUrl: string,
  timestampSec: number = 0
): Promise<string | null> {
  if (!videoUrl) return null

  const video = document.createElement('video')
  video.muted = true
  video.playsInline = true
  video.preload = 'auto'
  video.crossOrigin = 'anonymous'
  video.src = videoUrl

  try {
    await new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Video load timeout'))
      }, 15000)

      video.addEventListener('loadeddata', () => {
        clearTimeout(timeout)
        resolve()
      }, { once: true })
      video.addEventListener('error', (e) => {
        clearTimeout(timeout)
        reject(new Error(`Video load failed: ${e.type}`))
      }, { once: true })
    })

    const dataUrl = await captureFrameAt(video, timestampSec)
    return dataUrl
  } catch (err) {
    console.warn('[extractSingleFrameFromUrl] Failed:', err)
    return null
  } finally {
    video.src = ''
  }
}
